import { useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BackButton } from '@/components';
import { EventView } from '@/features/EventView';
import { EventViewSkeleton } from '@/features/EventView/components';
import { CustomEventView } from '@/features/CustomEventView';
import { EventFeed } from '@/features/EventFeed';
import { useReportEvent } from '@/features/ReportEventView/hooks/useReportEvent';
import { ReportEventActionBar } from '@/features/ReportEventView/components';
import { reportEventDetailToEvent } from '@/features/ReportEventView/utils/reportEventDetailToEvent';
import { getReportEventDisplayConfidence } from '@/utils/report-event-confidence';
import { useDocumentTitle } from '@/hooks';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { AppRoutes } from '@/constants/routes';
import {
    extractReportEventsFeedContext,
    useEventFeedStore,
} from '@/stores/event-feed.store';
import { ReportEventType, type ReportEvent } from '@/types/report';

export default function ReportEventPage() {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const { eventId } = useParams<{ eventId: string }>();
    const location = useLocation();
    const locationFeedContext = extractReportEventsFeedContext(location.state);
    const [eventTitle, setEventTitle] = useState<string | undefined>();
    const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
    const setFeedInteraction = useEventFeedStore(
        (state) => state.setFeedInteraction,
    );

    useDocumentTitle('event', eventTitle);

    const handleEventLoaded = useCallback((title: string) => {
        setEventTitle(title);
    }, []);

    const onFeedItemSelect = useCallback((item: ReportEvent) => {
        setSelectedEventId(item.id);

        const nextPath = AppRoutes.REPORT_EVENT.replace(
            ':eventId',
            encodeURIComponent(item.id),
        );
        const nextUrl = `${nextPath}${window.location.search}${window.location.hash}`;
        const currentUrl = [
            window.location.pathname,
            window.location.search,
            window.location.hash,
        ].join('');

        if (nextUrl !== currentUrl) {
            window.history.replaceState(window.history.state, '', nextUrl);
        }
    }, []);

    const playingEventId = selectedEventId ?? eventId ?? null;

    const { reportEvent, isLoading } = useReportEvent(playingEventId ?? '');

    useEffect(() => {
        setSelectedEventId(null);
    }, [eventId]);

    useEffect(() => {
        if (!playingEventId) return;
        setFeedInteraction(playingEventId, onFeedItemSelect);
        return () => setFeedInteraction(null, null);
    }, [playingEventId, onFeedItemSelect, setFeedInteraction]);

    if (!eventId) {
        return (
            <div className="p-8 text-white">{t('page.eventIdNotProvided')}</div>
        );
    }

    if (!reportEvent) {
        return isLoading ? (
            <EventViewSkeleton />
        ) : (
            <Navigate to={AppRoutes.REPORTS} replace />
        );
    }

    if (reportEvent.type === ReportEventType.CUSTOM) {
        return (
            <div className="h-full relative">
                {locationFeedContext && (
                    <EventFeed context={locationFeedContext} />
                )}
                <BackButton variant="floating" />
                <div className="h-full flex flex-col items-center">
                    <div className="w-full flex-1 min-h-0">
                        <CustomEventView
                            event={reportEventDetailToEvent(reportEvent)}
                            title={reportEvent.promptText ?? ''}
                            confidence={getReportEventDisplayConfidence({
                                confidence: reportEvent.confidence,
                                eventType: reportEvent.type,
                            })}
                            videoStartSeconds={reportEvent.videoStartSeconds}
                            videoEndSeconds={reportEvent.videoEndSeconds}
                            onEventLoaded={handleEventLoaded}
                            actionBar={
                                <ReportEventActionBar
                                    reportId={reportEvent.reportId}
                                    reportEvent={reportEvent}
                                />
                            }
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full relative">
            {locationFeedContext && <EventFeed context={locationFeedContext} />}
            <BackButton variant="floating" />

            <div className="h-full flex flex-col items-center">
                <div className="w-full flex-1 min-h-0">
                    <EventView
                        event={reportEventDetailToEvent(reportEvent)}
                        title={reportEvent.promptText ?? undefined}
                        onEventLoaded={handleEventLoaded}
                        actionBar={
                            <ReportEventActionBar
                                reportId={reportEvent.reportId}
                                reportEvent={reportEvent}
                            />
                        }
                    />
                </div>
            </div>
        </div>
    );
}
